import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { useParams } from "react-router-dom";
import { Spin, Tooltip, message, Divider, Empty, Input } from "antd";
import { LoadingOutlined, SearchOutlined, FolderAddOutlined } from "@ant-design/icons";
import ReactSelect from "react-select";
import "./AddToCollectionAdmin.scss";
import {
  handleBottomDrawer,
} from "../../actions/actionCreators";
import Folder from "../Folder/Folder";
import {
  getAllUserFolders,
  attachProductToFolder,
  createUserFolder,
} from "../../actions/user-folders-actions/actions";
import { getCollaborators } from "../../actions/order-actions/actions";
import PlusIcon from "../../icons/PlusIcon";

export interface IAddToCollectionAdminProps {
  setMode: ((mode: string) => void) | undefined;
}

export default function AddToCollectionAdmin({
  setMode,
}: IAddToCollectionAdminProps) {
  const dispatch = useDispatch();
  const { folders, isLoading } = useSelector((state: RootStateOrAny) => state.userFolders);
  const { user } = useSelector((state: RootStateOrAny) => state.auth);
  const { collaborators } = useSelector((state: RootStateOrAny) => state.orders);
  
  const [height, setHeight] = useState(window.innerHeight);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const params: any = useParams();

  useEffect(() => {
    dispatch(getAllUserFolders());
    dispatch(getCollaborators(""));
    
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch]);

  const filteredFolders = useMemo(() => {
    if (!folders) return [];
    return folders.filter((f: any) => 
      f.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [folders, searchTerm]);

  const handleCreate = () => {
    if (!formData.name) {
      message.error("Collection name is required");
      return;
    }
    dispatch(createUserFolder(formData));
    setShowForm(false);
    setFormData({ name: "", description: "" });
  };

  const handleConfirm = () => {
    if (!selectedFolderId) {
      message.error("Please select a collection");
      return;
    }
    dispatch(attachProductToFolder(selectedFolderId, params.productId));
    dispatch(handleBottomDrawer());
  };

  const variants = {
    enter: { y: height },
    center: { y: 0 },
    exit: { y: height },
  };

  return (
    <motion.div
      className="add_to_collection_admin_drawer"
      transition={{ type: "tween", duration: 0.4 }}
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
    >
      <div className="admin_drawer_inner">
        <div className="drawer_header">
          <div className="header_title">
            <h3>Admin Collections</h3>
            <p>Select a collection to attach this product</p>
          </div>
          <button className="close_drawer_btn" onClick={() => dispatch(handleBottomDrawer())}>
            ×
          </button>
        </div>

        {showForm ? (
          <div className="admin_create_form">
            <h4>Create New Collection</h4>
            <div className="input_group">
              <label>Name</label>
              <Input 
                placeholder="e.g. Premium Selection 2026" 
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
              />
            </div>
            <div className="input_group">
              <label>Description</label>
              <Input.TextArea 
                placeholder="Optional description..." 
                rows={3} 
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
              />
            </div>
            <div className="form_actions">
              <button className="btn_secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn_primary" onClick={handleCreate}>Create Collection</button>
            </div>
          </div>
        ) : (
          <>
            <div className="search_bar_container">
              <div className="search_input_wrapper">
                <SearchOutlined className="search_icon" />
                <input 
                  type="text" 
                  placeholder="Search collections..." 
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <Tooltip title="New Collection">
                <button className="add_new_btn" onClick={() => setShowForm(true)}>
                  <PlusIcon />
                </button>
              </Tooltip>
            </div>

            <div className="collections_grid_admin">
              {isLoading ? (
                <div className="loading_state_admin">
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
                </div>
              ) : filteredFolders.length > 0 ? (
                filteredFolders.map((folder: any) => (
                  <div 
                    key={folder.id} 
                    className={`admin_folder_card ${selectedFolderId === folder.id ? 'active' : ''}`}
                    onClick={() => setSelectedFolderId(folder.id)}
                  >
                    <div className="folder_icon_box">
                      <FolderAddOutlined />
                    </div>
                    <div className="folder_info">
                      <span className="folder_name">{folder.name}</span>
                      <span className="folder_count">{folder.products?.length || 0} Products</span>
                    </div>
                    <div className="selection_indicator"></div>
                  </div>
                ))
              ) : (
                <Empty description="No collections found" className="empty_state_admin" />
              )}
            </div>

            <div className="drawer_footer_admin">
              <button className="btn_cancel_admin" onClick={() => dispatch(handleBottomDrawer())}>
                Cancel
              </button>
              <button 
                className="btn_confirm_admin" 
                disabled={!selectedFolderId || isLoading}
                onClick={handleConfirm}
              >
                Attach Product
              </button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
